import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, TemplateRef } from '@angular/core';
import { NbTagInputAddEvent, NbDialogService } from '@nebular/theme';
import { ModuleMenuComponent } from '../module-menu/module-menu.component';
import { StripoService } from 'src/app/deebr/service/stripo.service';
import { UserService } from 'src/app/deebr/service/user.service';
import { User } from 'src/app/deebr/store/user.store';
import { Subject, takeUntil, switchMap, catchError, of, finalize } from 'rxjs';
import { Router } from '@angular/router';
import { TourService, TourStep } from 'src/app/deebr/service/tour.service';

interface StripoModule {
  id: number;
  key: string;
  name: string;
  html: string;
  css: string;
  width: number;
  height: number;
  blockType: string;
  icon: string;
  croppedIcon: string;
  description: string;
  tags: string[];
}

@Component({
  selector: 'app-module-list',
  templateUrl: './module-list.component.html',
  styleUrls: ['./module-list.component.scss'],
  standalone: false
})
export class ModuleListComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly TOUR_KEY = 'deebr_tour_modules_seen';
  private destroy$ = new Subject<void>();
  public modules: StripoModule[] = [];
  public loading: boolean = false;
  public loadingMore: boolean = false;
  public userId!: number;
  public userName: string = '';
  public errorMessage: string = '';
  public displayMode: string = 'grid';
  public searchTerm: string = '';
  public filteredModules: StripoModule[] = [];
  public totalModules: number = 0;
  public stripoTags: any[] = [];
  public selectedTags: any[] = [];
  public filteredTags: any[] = [];
  public isEditPanelOpen: boolean = false;
  public editingModule: StripoModule | null = null;
  
  // Infinite scroll properties
  private offset: number = 0;
  private limit: number = 28;
  private authToken: string = '';
  private stripoUserKey: string = '';
  private emailId: string = '';
  private userIdStr: string = '';

  @ViewChild('sentinel', { read: ElementRef }) sentinel!: ElementRef;
  @ViewChild('deleteModuleDialog') deleteModuleDialog!: TemplateRef<any>;
  private intersectionObserver?: IntersectionObserver;

  constructor(
    private stripoService: StripoService,
    private userService: UserService,
    private dialogService: NbDialogService,
    private router: Router,
    private tourService: TourService
  ) {}

  ngOnInit(): void {
    const savedDisplayMode = sessionStorage.getItem('displayMode');
    if (savedDisplayMode) {
      this.displayMode = savedDisplayMode;
    }

    this.initializeModules();
    this.autoStartTour();
  }

  ngOnDestroy(): void {
    // Disconnect the intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Set up Intersection Observer to detect when sentinel element becomes visible
    this.setupIntersectionObserver();
  }

  private setupIntersectionObserver(): void {
    const options = {
      root: null, // viewport
      rootMargin: '200px', // trigger 200px before the sentinel is visible
      threshold: 0.1
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadMoreModules();
        }
      });
    }, options);

    // Start observing the sentinel element
    if (this.sentinel && this.sentinel.nativeElement) {
      this.intersectionObserver.observe(this.sentinel.nativeElement);
    } else {
      // Retry after a short delay
      setTimeout(() => {
        if (this.sentinel && this.sentinel.nativeElement) {
          this.intersectionObserver?.observe(this.sentinel.nativeElement);
        }
      }, 100);
    }
  }

  private initializeModules(): void {
    this.loading = true;
    this.userService.getUsers().pipe(
      takeUntil(this.destroy$),
      switchMap((users: User[]) => {
        if (!users || users.length === 0) {
          throw new Error('User not found');
        }
        this.userId = users[0].id;
        this.userName = users[0].first_name;
        return this.stripoService.getAuthToken();
      }),
      switchMap((authResponse: { token: string }) => {
        this.authToken = authResponse.token;
        const envPrefix = window.location.hostname === 'app.deebr.co' ? 'prod-' : 'preprod-';
        this.emailId = envPrefix + "0";
        this.userIdStr = envPrefix + this.userId.toString();
        const username = this.userName || 'User';

        return this.stripoService.getConfig(this.authToken, this.emailId, this.userIdStr, username).pipe(
          switchMap((config: any) => {
            if (config.blocksLibrary && config.blocksLibrary.tabsV2 && config.blocksLibrary.tabsV2.length > 0) {
              this.stripoUserKey = config.blocksLibrary.tabsV2[0].key;
              console.log('First Tab Key:', this.stripoUserKey);
                  
              // Now call the custom blocks API
              this.stripoService.getCustomBlocks(
                this.authToken,
                this.stripoUserKey,
                this.emailId,
                this.userIdStr,
                username
              ).subscribe({
                next: (customBlocks) => {
                  console.log('Custom Blocks Response:', customBlocks);
                  if (customBlocks && customBlocks.tags) {
                    this.stripoTags = customBlocks.tags;
                    this.updateFilteredTags();
                    console.log('Stripo Tags:', this.stripoTags);
                  }
                  // Add "Aucune catégorie" tag
                  const noCategoryTag = { id: -1, value: 'Aucune catégorie' };
                  this.stripoTags.push(noCategoryTag);
                  this.updateFilteredTags();
                },
                error: (err) => {
                  console.error('Error fetching custom blocks:', err);
                }
              });
              
              // Get total modules count
              const tagIds = this.selectedTags.map(t => t.id).join(',');
              return this.stripoService.getMaxModules(this.authToken, this.stripoUserKey, this.emailId, this.userIdStr, username, tagIds).pipe(
                switchMap((maxResponse: any) => {
                  if (maxResponse.modules && maxResponse.modules.length > 0) {
                    this.totalModules = maxResponse.modules[0].total || 0;
                  }

                  // Load first batch
                  return this.stripoService.getMoreModules(
                    this.authToken, 
                    this.stripoUserKey, 
                    this.emailId, 
                    this.userIdStr, 
                    username, 
                    this.limit,
                    this.offset,
                    tagIds
                  );
                })
              );
            } else {
              throw new Error('No blocks library config found');
            }
          })
        );
      }),
      catchError(err => {
        console.error('Error loading modules:', err);
        this.errorMessage = 'Erreur lors du chargement des modules.';
        return of(null);
      }),
      finalize(() => this.loading = false)
    ).subscribe((response: any) => {
      if (Array.isArray(response)) {
        this.modules = response;
        this.filteredModules = [...this.modules];
        this.offset += this.limit; // Update offset for next load
      }
    });
  }

  private loadMoreModules(): void {
    // Check if we should load more
    if (this.loadingMore || this.loading) {
      return;
    }
    
    if (this.modules.length >= this.totalModules) {
      return;
    }

    // Check if search is active - don't load more during search
    if (this.searchTerm) {
      return;
    }
    
    // Check if we have auth details
    if (!this.authToken || !this.stripoUserKey) {
      return;
    }

    this.loadingMore = true;

    this.stripoService.getMoreModules(
      this.authToken,
      this.stripoUserKey,
      this.emailId,
      this.userIdStr,
      this.userName || 'User',
      this.limit,
      this.offset,
      this.selectedTags.map(t => t.id).join(',')
    ).pipe(
      takeUntil(this.destroy$),
      catchError(err => {
        console.error('Error loading more modules:', err);
        return of([]);
      }),
      finalize(() => {
        this.loadingMore = false;
      })
    ).subscribe((response: any) => {
      if (Array.isArray(response) && response.length > 0) {
        this.modules = [...this.modules, ...response];
        this.filteredModules = [...this.modules];
        this.offset += this.limit; // Update offset for next load
      }
    });
  }

  onDisplayModeChange(): void {
    sessionStorage.setItem('displayMode', this.displayMode);
  }

  onSearchTermChange(term: string): void {
    this.searchTerm = term;
    this.filterModules();
  }

  filterModules(): void {
    if (!this.searchTerm) {
      this.filteredModules = [...this.modules];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredModules = this.modules.filter(m => 
        m.name?.toLowerCase().includes(term)
      );
    }
  }

  private tagInputValue: string = '';

  onTagInputChange(event: Event): void {
    this.tagInputValue = (event.target as HTMLInputElement).value;
    this.updateFilteredTags();
  }

  private updateFilteredTags(): void {
    const search = this.tagInputValue.toLowerCase();
    this.filteredTags = this.stripoTags.filter(
      t => !this.selectedTags.find(s => s.id === t.id)
        && (!search || t.value.toLowerCase().includes(search))
    );
  }

  onTagAdd({ value, input }: NbTagInputAddEvent): void {
    if (value) {
      const foundTag = this.stripoTags.find(t => t.value.toLowerCase() === value.toLowerCase());
      if (foundTag && !this.selectedTags.find(t => t.id === foundTag.id)) {
        this.selectedTags.push(foundTag);
      }
    }
    this.tagInputValue = '';
    input.nativeElement.value = '';
    this.updateFilteredTags();
    this.reloadModules();
  }

  onTagRemove(tagToRemove: any): void {
    this.selectedTags = this.selectedTags.filter(t => t.id !== tagToRemove.id);
    this.updateFilteredTags();
    this.reloadModules();
  }

  onTagSelected(tag: any, input: any): void {
     if (tag && !this.selectedTags.find(t => t.id === tag.id)) {
        this.selectedTags.push(tag);
     }
     this.tagInputValue = '';
     if (input) {
       input.value = '';
     }
     this.updateFilteredTags();
     this.reloadModules();
  }

  reloadModules(): void {
    this.offset = 0;
    this.modules = [];
    this.filteredModules = [];
    this.initializeModules();
  }

  openEditPanel(module: StripoModule): void {
    this.editingModule = module;
    this.isEditPanelOpen = true;
  }

  closeEditPanel(): void {
    this.isEditPanelOpen = false;
    this.editingModule = null;
  }

  openModuleInEditor(moduleId?: number): void {
    const targetId = moduleId ?? this.editingModule?.id;
    if (!targetId) {
      return;
    }

    const selectedModule = this.modules.find((module) => module.id === targetId) ?? this.editingModule;
    this.router.navigate(['/pages/modules', targetId], {
      state: { module: selectedModule },
    });
  }

  onModuleUpdate(formData: any): void {
    const envPrefix = window.location.hostname === 'app.deebr.co' ? 'prod-' : 'preprod-';
    if (!this.editingModule) return;

    const payload = {
        name: formData.name,
        description: formData.description,
        tags: formData.tags,
        key: `email_${envPrefix + this.userId}`,
    };

    this.stripoService.updateCustomBlock(
        this.authToken,
        this.emailId,
        this.userIdStr,
        this.userName,
        this.editingModule.id,
        payload
    ).subscribe({
        next: (response) => {
            console.log('Module updated successfully', response);
            this.closeEditPanel();
            this.reloadModules(); // Refresh list
        },
        error: (err) => {
            console.error('Error updating module', err);
            // Optionally show error message
        }
    });
  }

  onModuleDelete(): void {
    if (!this.editingModule) return;
    this.dialogService.open(this.deleteModuleDialog);
  }

  confirmDeleteModule(dialogRef: any): void {
    if (!this.editingModule) return;

    this.stripoService.deleteCustomBlock(
      this.authToken,
      this.emailId,
      this.userIdStr,
      this.userName,
      this.editingModule.id,
      this.stripoUserKey
    ).subscribe({
      next: () => {
        console.log('Module deleted successfully');
        dialogRef.close();
        this.closeEditPanel();
        this.reloadModules();
      },
      error: (err) => {
        console.error('Error deleting module', err);
        dialogRef.close();
      }
    });
  }

  startTour(): void {
    let demoInjected = false;
    if (this.filteredModules.length === 0) {
      const demoModule: StripoModule = {
        id: -1,
        key: 'demo-module',
        name: 'Module de démonstration',
        html: '',
        css: '',
        width: 600,
        height: 400,
        blockType: 'basic',
        icon: '',
        croppedIcon: '',
        description: 'Ceci est un module de démonstration pour le tour guidé.',
        tags: []
      };
      this.modules = [demoModule];
      this.filteredModules = [demoModule];
      demoInjected = true;
    }

    const removeDemoIfNeeded = () => {
      if (demoInjected) {
        this.modules = this.modules.filter(m => m.id !== -1);
        this.filteredModules = this.filteredModules.filter(m => m.id !== -1);
      }
      this.closeEditPanel();
    };

    const steps: TourStep[] = [
      {
        id: 'welcome',
        title: 'Bienvenue dans la bibliothèque de modules',
        text: 'Cette page vous permet de <b>consulter, rechercher et modifier</b> vos modules d\'email. Découvrons les fonctionnalités principales.',
      },
      {
        id: 'tags-filter',
        title: 'Filtrage par balises',
        text: 'Utilisez les <b>balises</b> pour filtrer vos modules par catégorie. Saisissez un mot-clé dans le champ et appuyez sur Entrée pour ajouter un filtre — les modules s\'actualisent automatiquement.<br><br>La balise <b>Aucune catégorie</b> permet de retrouver les modules sans balise.',
        attachTo: { element: '[data-tour="module-tags"]', on: 'bottom' },
      },
      {
        id: 'search',
        title: 'Recherche par nom',
        text: 'Tapez le <b>nom d\'un module</b> dans ce champ pour le retrouver rapidement. La recherche filtre instantanément les modules déjà chargés.',
        attachTo: { element: '[data-tour="module-search"]', on: 'bottom' },
      },
      {
        id: 'display-mode',
        title: 'Mode d\'affichage',
        text: 'Choisissez entre trois modes d\'affichage :<br><br>• <b>Grille</b> — aperçu large avec prévisualisation de l\'image<br>• <b>Petite grille</b> — plus de modules visibles à l\'écran<br>• <b>Liste</b> — vue compacte en lignes avec nom et description',
        attachTo: { element: '[data-tour="module-display-mode"]', on: 'bottom' },
      },
      {
        id: 'module-grid',
        title: 'Grille des modules',
        text: 'Vos modules sont affichés ici sous forme de <b>cartes</b>. Les modules se chargent automatiquement au fur et à mesure que vous faites défiler la page (<b>défilement infini</b>).',
        attachTo: { element: '[data-tour="module-grid"]', on: 'top' },
      },
      {
        id: 'module-card',
        title: 'Carte de module',
        text: 'Chaque carte affiche un <b>aperçu visuel</b> du module, son <b>nom</b> et sa <b>description</b>.<br><br>Au survol, l\'aperçu s\'agrandit pour une meilleure visibilité. <b>Cliquez</b> sur une carte pour ouvrir le panneau d\'édition.',
        attachTo: { element: '[data-tour="module-card"]', on: 'right' },
      },
      {
        id: 'side-panel',
        title: 'Panneau de détails',
        text: 'Le panneau latéral s\'ouvre à droite lorsque vous cliquez sur un module. Il vous permet de <b>modifier les informations</b> du module : aperçu, nom, description et balises.',
        attachTo: { element: '[data-tour="module-side-panel"]', on: 'left' },
        beforeShowPromise: () => new Promise<void>(resolve => {
          const firstModule = this.filteredModules[0];
          if (firstModule && !this.isEditPanelOpen) {
            this.openEditPanel(firstModule);
          }
          setTimeout(resolve, 300);
        }),
      },
      {
        id: 'open-editor',
        title: 'Ouvrir dans l\'éditeur',
        text: 'Ce bouton ouvre le module dans l\'<b>éditeur visuel complet</b> pour modifier son contenu HTML, sa structure et son design.',
        attachTo: { element: '[data-tour="module-open-editor"]', on: 'top' },
      },
      {
        id: 'save-btn',
        title: 'Enregistrer les modifications',
        text: 'Cliquez sur <b>Mettre à jour</b> pour sauvegarder les modifications apportées au nom, à la description et aux balises du module.',
        attachTo: { element: '[data-tour="module-save-btn"]', on: 'top' },
      },
    ];

    this.tourService.start(ModuleListComponent.TOUR_KEY, steps, false, removeDemoIfNeeded);
  }

  private autoStartTour(): void {
    if (this.tourService.isCompleted(ModuleListComponent.TOUR_KEY)) return;
    setTimeout(() => this.startTour(), 2000);
  }
}
